<template>
  <!-- Popover de UN elemento (al hacer click en el modo foco). Overlay dentro del
       canvasContainer: la línea conectora no intercepta el ratón; la tarjeta sí. Trae los
       datos por servicio (componente→servicio→api). Diseño pro, azul de la app (#3a6bc8). -->
  <div class="epc-elpop-overlay">
    <!-- Línea conectora elemento → tarjeta -->
    <svg class="epc-elpop-svg">
      <line :x1="anchor.x" :y1="anchor.y" :x2="line.x2" :y2="line.y2"
        stroke="#3a6bc8" stroke-width="1.5" stroke-dasharray="4 3" stroke-opacity="0.6"></line>
      <circle :cx="anchor.x" :cy="anchor.y" r="3.5" fill="#ffffff" stroke="#3a6bc8" stroke-width="1.5"></circle>
    </svg>

    <!-- Tarjeta -->
    <div ref="card" class="epc-elpop-card" :style="cardStyle">
      <!-- Cabecera -->
      <div class="epc-elpop-head">
        <span class="epc-elpop-head-title">
          <span class="epc-elpop-type">{{ $t(source.type) }}</span>
          <span class="epc-elpop-name">{{ displayName }}</span>
        </span>
        <button class="epc-elpop-close" type="button" :aria-label="$t('close')" @click="$emit('epc-close')">
          <b-icon icon="xmark" pack="fas" size="is-small"></b-icon>
        </button>
      </div>

      <!-- Loader -->
      <div v-if="loading" class="epc-elpop-loading">
        <b-icon icon="spinner" pack="fas" size="is-small" custom-class="fa-spin"></b-icon>
        <span>{{ $t('loading_information') }}</span>
      </div>

      <template v-else>
        <b-tabs type="is-boxed" size="is-small" v-model="activeTab" class="epc-elpop-tabs">
          <!-- Progreso -->
          <b-tab-item>
            <template #header>
              <b-icon icon="history" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('progress') }}</span>
            </template>
            <div class="epc-elpop-current">
              <span class="epc-elpop-dot" :style="{ backgroundColor: currentColor('progress') }"></span>
              <strong>{{ $t('current_status') }}</strong>
              <span class="ml-1">{{ currentName('progress') }}</span>
            </div>
            <div class="epc-elpop-history">
              <p v-if="history.progress.length === 0" class="epc-elpop-empty">{{ $t('no_progress_history') }}</p>
              <div v-for="(item, i) in history.progress" :key="'p-' + i" class="epc-elpop-item">
                <span class="epc-elpop-dot epc-elpop-dot--sm" :style="{ backgroundColor: item.state_color || '#666' }"></span>
                <div class="epc-elpop-item-body">
                  <div class="epc-elpop-item-state">{{ item.state_name }}</div>
                  <div class="epc-elpop-item-date">{{ item.created_at }}</div>
                  <div class="epc-elpop-item-user">
                    <img v-if="item.user && item.user.avatar" :src="item.user.avatar"
                      :alt="item.user.fullname || $t('user')" class="epc-elpop-avatar" />
                    <span>{{ (item.user && item.user.fullname) || $t('user') }}</span>
                    <span v-if="item.user && item.user.area" class="epc-elpop-area"> ({{ item.user.area }})</span>
                  </div>
                </div>
              </div>
            </div>
          </b-tab-item>

          <!-- Incidencia -->
          <b-tab-item>
            <template #header>
              <b-icon icon="exclamation-triangle" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('issue') }}</span>
            </template>
            <div v-if="currentStatus.incident" class="epc-elpop-current">
              <span class="epc-elpop-dot" :style="{ backgroundColor: currentColor('incident') }"></span>
              <strong>{{ $t('current_issue') }}</strong>
              <span class="ml-1">{{ currentName('incident') }}</span>
            </div>
            <div class="epc-elpop-history">
              <p v-if="history.incidents.length === 0" class="epc-elpop-empty">{{ $t('no_incident_history') }}</p>
              <div v-for="(item, i) in history.incidents" :key="'i-' + i" class="epc-elpop-item">
                <span class="epc-elpop-dot epc-elpop-dot--sm" :style="{ backgroundColor: item.state_color || '#dc2626' }"></span>
                <div class="epc-elpop-item-body">
                  <div class="epc-elpop-item-state">{{ item.state_name }}</div>
                  <div class="epc-elpop-item-date">{{ item.created_at }}</div>
                  <div class="epc-elpop-item-user">
                    <img v-if="item.user && item.user.avatar" :src="item.user.avatar"
                      :alt="item.user.fullname || $t('user')" class="epc-elpop-avatar" />
                    <span>{{ (item.user && item.user.fullname) || $t('user') }}</span>
                    <span v-if="item.user && item.user.area" class="epc-elpop-area"> ({{ item.user.area }})</span>
                  </div>
                  <div v-if="item.description" class="epc-elpop-item-desc">{{ item.description }}</div>
                </div>
              </div>
            </div>
          </b-tab-item>

          <!-- Certificación (solo si el elemento ya está certificado) -->
          <b-tab-item v-if="certification">
            <template #header>
              <b-icon icon="certificate" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('certification') }}</span>
            </template>
            <div class="epc-elpop-cert">
              <div class="epc-elpop-cert-badge">
                <b-icon icon="check-circle" pack="fas" size="is-small"></b-icon>
                <span>{{ $t('already_certified') }}</span>
              </div>
              <ul class="epc-elpop-kv">
                <li v-if="certification.certification_number" class="epc-elpop-kv-row">
                  <span class="epc-elpop-kv-label">{{ $t('certification_number') }}</span>
                  <span class="epc-elpop-kv-value">{{ certSequence(certification.certification_number) }}</span>
                </li>
                <li class="epc-elpop-kv-row">
                  <span class="epc-elpop-kv-label">{{ $t('work_order') }}</span>
                  <span class="epc-elpop-kv-value">{{ certification.order_code || '-' }}</span>
                </li>
                <li class="epc-elpop-kv-row">
                  <span class="epc-elpop-kv-label">{{ $t('date') }}</span>
                  <span class="epc-elpop-kv-value">{{ certification.date || '-' }}</span>
                </li>
                <li class="epc-elpop-kv-row">
                  <span class="epc-elpop-kv-label">{{ $t('contractor') }}</span>
                  <span class="epc-elpop-kv-value">{{ certification.contractor || '-' }}</span>
                </li>
              </ul>
              <a v-if="certification.validation_flow_id" :href="certificationFlowUrl(certification.validation_flow_id)"
                target="_blank" rel="noopener" class="epc-elpop-cert-link">
                <b-icon icon="external-link-alt" pack="fas" size="is-small"></b-icon>
                <span>{{ $t('view_certification_flow') }}</span>
              </a>
            </div>
          </b-tab-item>

          <!-- Atributos extra -->
          <b-tab-item :disabled="attributesList.length === 0">
            <template #header>
              <b-icon icon="list-ul" pack="fas" size="is-small"></b-icon>
              <span class="ml-1">{{ $t('attributes') }}</span>
            </template>
            <p v-if="attributesList.length === 0" class="epc-elpop-empty">{{ $t('no_extra_attributes') }}</p>
            <ul v-else class="epc-elpop-kv">
              <li v-for="attr in attributesList" :key="attr.key" class="epc-elpop-kv-row">
                <span class="epc-elpop-kv-label">{{ attr.label }}</span>
                <span class="epc-elpop-kv-value">{{ attr.value }}</span>
              </li>
            </ul>
          </b-tab-item>
        </b-tabs>
      </template>
    </div>
  </div>
</template>

<script>
import { fetchPictogramComponent } from "../../services/power-plant.service"

const CARD_WIDTH = 300
const GAP = 64        // separación entre el elemento y la tarjeta (línea más larga)
const MARGIN = 12     // margen mínimo a los bordes del contenedor

export default {
  name: "ViewerElementPopover",

  props: {
    // Identifica el elemento a consultar: { pictogramId, componentId, type, name }.
    source: { type: Object, required: true },
    // Centro de la entidad en px del contenedor (ancla de la línea).
    anchor: { type: Object, required: true },
    // Rect de la entidad en px del contenedor { left, top, right, bottom } (para no taparla).
    box: { type: Object, required: true },
    // Tamaño del contenedor para recortar la tarjeta dentro { width, height }.
    bounds: { type: Object, required: true },
  },

  data() {
    return {
      loading: true,
      activeTab: 0,
      history: { progress: [], incidents: [] },
      currentStatus: { progress: null, incident: null },
      extraAttributes: null,
      componentMetadata: null,
      certification: null,
      cardHeight: 260, // se mide tras render
    }
  },

  computed: {
    displayName() {
      const code = this.componentMetadata && this.componentMetadata.code
      if (code != null && code !== '') return code
      const name = this.componentMetadata && this.componentMetadata.name
      if (name != null && name !== '') return name
      return this.source.name != null ? String(this.source.name) : ''
    },

    // Lado y posición de la tarjeta: se coloca hacia el lado con MÁS espacio (así se aleja
    // del borde y la línea queda larga); vertical centrada en el ancla y recortada al
    // contenedor.
    placement() {
      const w = CARD_WIDTH
      const h = this.cardHeight
      const bw = this.bounds.width || 0
      const bh = this.bounds.height || 0

      const roomRight = bw - this.box.right
      const roomLeft = this.box.left
      const side = roomRight >= roomLeft ? 'right' : 'left'

      let x = side === 'right' ? this.box.right + GAP : this.box.left - GAP - w
      if (x < MARGIN) x = MARGIN
      if (x + w > bw - MARGIN) x = Math.max(MARGIN, bw - MARGIN - w)

      let y = this.anchor.y - h / 2
      if (y < MARGIN) y = MARGIN
      if (y + h > bh - MARGIN) y = Math.max(MARGIN, bh - MARGIN - h)

      return { side, x, y, w, h }
    },

    cardStyle() {
      return { top: this.placement.y + 'px', left: this.placement.x + 'px', width: CARD_WIDTH + 'px' }
    },

    // Punto de la tarjeta al que llega la línea: borde interior (más cercano a la entidad),
    // a la altura del ancla pero dentro de la tarjeta.
    line() {
      const p = this.placement
      const x2 = p.side === 'right' ? p.x : p.x + p.w
      const y2 = Math.max(p.y + 12, Math.min(this.anchor.y, p.y + p.h - 12))
      return { x2, y2 }
    },

    attributesList() {
      if (!this.extraAttributes) return []
      const omit = new Set(['id', 'updated_at', 'updated_by'])
      return Object.keys(this.extraAttributes)
        .filter(k => !omit.has(k))
        .map(k => ({ key: k, label: this._humanize(k), value: this.extraAttributes[k] }))
        .filter(a => a.value !== null && a.value !== undefined && a.value !== '')
    },
  },

  watch: {
    // Al cambiar de elemento (click en otro), recargar.
    'source.componentId'() {
      this._Load()
    },
  },

  mounted() {
    this._Load()
  },

  updated() {
    this._MeasureCard()
  },

  methods: {
    async _Load() {
      this.loading = true
      this.history = { progress: [], incidents: [] }
      this.currentStatus = { progress: null, incident: null }
      this.extraAttributes = null
      this.componentMetadata = null
      this.certification = null

      try {
        const { data } = await fetchPictogramComponent(this.source.pictogramId, this.source.componentId, this.source.type)
        if (data && data.success && data.component) {
          const c = data.component
          this.history = c.history || { progress: [], incidents: [] }
          this.currentStatus = c.current_status || { progress: null, incident: null }
          this.extraAttributes = c.extra_attributes || null
          this.componentMetadata = c.metadata || null
          this.certification = c.certification || null
        }
      } catch (e) {
        this.$notify && this.$notify.error('error_loading_component_info')
      } finally {
        this.loading = false
        this.$nextTick(() => this._MeasureCard())
      }
    },

    _MeasureCard() {
      const el = this.$refs.card
      if (el && el.offsetHeight > 0 && el.offsetHeight !== this.cardHeight) {
        this.cardHeight = el.offsetHeight
      }
    },

    currentName(type) {
      const cur = this.currentStatus[type]
      if (cur && cur.state_name) return cur.state_name
      return type === 'progress' ? this.$t('no_status_2') : this.$t('without_issue')
    },

    currentColor(type) {
      const cur = this.currentStatus[type]
      return (cur && cur.state_color) || '#666'
    },

    // Nº de certificación: último segmento numérico sin ceros a la izquierda (igual que el PDF).
    certSequence(certification) {
      if (!certification) return ''
      const parts = String(certification).split('-')
      const last = parts[parts.length - 1]
      return /^\d+$/.test(last) ? String(parseInt(last, 10)) : String(certification)
    },

    certificationFlowUrl(flowId) {
      if (!flowId) return '#'
      try {
        return this.$router.resolve({ name: 'validation.home', params: { validation_id: flowId } }).href
      } catch (e) {
        return `/validation/${flowId}`
      }
    },

    _humanize(key) {
      return String(key).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    },
  },
}
</script>

<style scoped>
.epc-elpop-overlay {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 8;
}

.epc-elpop-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
}

.epc-elpop-card {
  position: absolute;
  pointer-events: auto;
  background: #ffffff;
  border: 1px solid rgba(58, 107, 200, 0.35);
  border-radius: 10px;
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.22), 0 3px 8px rgba(17, 24, 39, 0.12);
  overflow: hidden;
  animation: epcElPopIn 0.14s ease;
}

/* Cabecera */
.epc-elpop-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 12px;
  background: linear-gradient(90deg, #3a6bc8, #4f7bd4);
  color: #ffffff;
}

.epc-elpop-head-title {
  display: flex;
  align-items: baseline;
  gap: 7px;
  min-width: 0;
}

.epc-elpop-type {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.85;
}

.epc-elpop-name {
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.epc-elpop-close {
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.14);
  color: #ffffff;
  cursor: pointer;
  transition: background-color 0.12s ease;
}

.epc-elpop-close:hover {
  background: rgba(255, 255, 255, 0.28);
}

/* Loader */
.epc-elpop-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 26px 12px;
  font-size: 12px;
  color: #6b7280;
}

/* Estado actual */
.epc-elpop-current {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #374151;
  padding: 10px 12px;
  border-bottom: 1px solid #f0f1f6;
}

.epc-elpop-dot {
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
}

.epc-elpop-dot--sm {
  width: 10px;
  height: 10px;
}

/* Historial */
.epc-elpop-history {
  max-height: 210px;
  overflow-y: auto;
  padding: 10px 12px;
}

.epc-elpop-empty {
  font-size: 12px;
  color: #9aa2ad;
  text-align: center;
  padding: 8px 0;
}

.epc-elpop-item {
  display: flex;
  gap: 8px;
  padding-bottom: 9px;
  margin-bottom: 9px;
  border-bottom: 1px solid #f3f4f6;
}

.epc-elpop-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.epc-elpop-item .epc-elpop-dot--sm {
  margin-top: 2px;
}

.epc-elpop-item-body {
  min-width: 0;
}

.epc-elpop-item-state {
  font-size: 12px;
  font-weight: 700;
  color: #1f2937;
}

.epc-elpop-item-date {
  font-size: 11px;
  color: #9aa2ad;
}

.epc-elpop-item-user {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  font-size: 11px;
  color: #6b7280;
}

.epc-elpop-avatar {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.epc-elpop-area {
  color: #9aa2ad;
}

.epc-elpop-item-desc {
  margin-top: 3px;
  font-size: 11px;
  font-style: italic;
  color: #6b7280;
  word-break: break-word;
}

/* Certificación + atributos (listas clave/valor) */
.epc-elpop-cert {
  padding: 12px;
}

.epc-elpop-cert-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 11px;
  padding: 4px 11px;
  border-radius: 999px;
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #86efac;
  font-weight: 700;
  font-size: 11px;
}

.epc-elpop-kv {
  list-style: none;
  margin: 0 12px 12px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  overflow: hidden;
}

.epc-elpop-cert .epc-elpop-kv {
  margin: 0;
}

.epc-elpop-kv-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 7px 11px;
  font-size: 11.5px;
  border-bottom: 1px solid #f3f4f6;
}

.epc-elpop-kv-row:last-child {
  border-bottom: none;
}

.epc-elpop-kv-row:nth-child(odd) {
  background: #f9fafb;
}

.epc-elpop-kv-label {
  color: #6b7280;
  font-weight: 500;
  flex: none;
}

.epc-elpop-kv-value {
  color: #111827;
  font-weight: 700;
  text-align: right;
  word-break: break-word;
}

.epc-elpop-cert-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 11px;
  padding: 6px 11px;
  border-radius: 6px;
  background: #eef3fc;
  color: #3a6bc8;
  border: 1px solid #cdddf4;
  font-weight: 600;
  font-size: 11.5px;
  text-decoration: none;
}

.epc-elpop-cert-link:hover {
  background: #e2ebfa;
}

/* Tabs Buefy compactos + acento azul app */
.epc-elpop-tabs ::v-deep .tabs {
  margin-bottom: 0;
}

.epc-elpop-tabs ::v-deep .tabs ul {
  border-bottom-color: #eceef3;
}

.epc-elpop-tabs ::v-deep .tabs li a {
  padding: 6px 9px;
  font-size: 11.5px;
  border-radius: 0;
  color: #6b7280;
}

.epc-elpop-tabs ::v-deep .tabs li.is-active a {
  color: #3a6bc8;
  border-bottom-color: #3a6bc8;
}

.epc-elpop-tabs ::v-deep .tab-content {
  padding: 0;
}

@keyframes epcElPopIn {
  from { opacity: 0; transform: translateY(-3px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
